import React, { useMemo, useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/Layout';
import { assessmentsService, AttendancePayload, AssessmentAttendanceEntry } from '../api/assessmentsService';
import { useAuth } from '../contexts/AuthContext';

const AttendanceEditor: React.FC<{
  entry: AssessmentAttendanceEntry;
  onSave: (attendanceId: number, payload: AttendancePayload) => void;
  isSaving: boolean;
}> = ({ entry, onSave, isSaving }) => {
  const [form, setForm] = useState({
    displayName: entry.displayName || entry.resolvedName || '',
    email: entry.email || '',
    phone: entry.phone || '',
    isAttending: entry.isAttending,
    hotelNeeded: entry.hotelNeeded,
    hotelSundayNight: entry.hotelSundayNight || false,
    hotelMondayNight: entry.hotelMondayNight || false,
    guestCount: entry.guestCount ?? 0,
    guestNames: (entry.guestNames || []).join(', '),
    dietaryRestrictions: entry.dietaryRestrictions || '',
  });

  useEffect(() => {
    setForm({
      displayName: entry.displayName || entry.resolvedName || '',
      email: entry.email || '',
      phone: entry.phone || '',
      isAttending: entry.isAttending,
      hotelNeeded: entry.hotelNeeded,
      hotelSundayNight: entry.hotelSundayNight || false,
      hotelMondayNight: entry.hotelMondayNight || false,
      guestCount: entry.guestCount ?? 0,
      guestNames: (entry.guestNames || []).join(', '),
      dietaryRestrictions: entry.dietaryRestrictions || '',
    });
  }, [entry.id, entry.updatedAt]);

  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-semibold text-gray-900">{entry.resolvedName || entry.displayName || 'Attendee'}</div>
        <div className="text-xs text-gray-500">{entry.user?.email || entry.email || '—'}</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <input
          className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          placeholder="Name"
          value={form.displayName}
          onChange={(e) => setForm((prev) => ({ ...prev, displayName: e.target.value }))}
        />
        <input
          className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          placeholder="Email"
          value={form.email}
          onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
        />
        <input
          className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          placeholder="Phone"
          value={form.phone}
          onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
        />
      </div>
      <div className="flex flex-wrap gap-4 text-sm text-gray-700">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.isAttending}
            onChange={(e) => setForm((prev) => ({ ...prev, isAttending: e.target.checked }))}
          />
          Attending
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.hotelNeeded}
            onChange={(e) => setForm((prev) => ({ ...prev, hotelNeeded: e.target.checked }))}
          />
          Hotel needed
        </label>
        {form.hotelNeeded && (
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.hotelSundayNight}
                onChange={(e) => setForm((prev) => ({ ...prev, hotelSundayNight: e.target.checked }))}
              />
              Sunday night
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.hotelMondayNight}
                onChange={(e) => setForm((prev) => ({ ...prev, hotelMondayNight: e.target.checked }))}
              />
              Monday night
            </label>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <input
          className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          type="number"
          min={0}
          placeholder="Guest count"
          value={form.guestCount}
          onChange={(e) => setForm((prev) => ({ ...prev, guestCount: Number(e.target.value) }))}
        />
        <input
          className="border border-gray-300 rounded-md px-3 py-2 text-sm md:col-span-2"
          placeholder="Guest names (comma separated)"
          value={form.guestNames}
          onChange={(e) => setForm((prev) => ({ ...prev, guestNames: e.target.value }))}
        />
      </div>
      <textarea
        className="border border-gray-300 rounded-md px-3 py-2 text-sm w-full"
        rows={2}
        placeholder="Dietary restrictions"
        value={form.dietaryRestrictions}
        onChange={(e) => setForm((prev) => ({ ...prev, dietaryRestrictions: e.target.value }))}
      />
      <button
        className="inline-flex items-center px-4 py-2 rounded-md bg-primary-600 text-white text-sm hover:bg-primary-700 disabled:opacity-50"
        disabled={isSaving}
        onClick={() => onSave(entry.id, {
          displayName: form.displayName.trim() || null,
          email: form.email.trim() || null,
          phone: form.phone.trim() || null,
          isAttending: form.isAttending,
          hotelNeeded: form.hotelNeeded,
          hotelSundayNight: form.hotelSundayNight,
          hotelMondayNight: form.hotelMondayNight,
          guestCount: form.guestCount || 0,
          guestNames: form.guestNames,
          dietaryRestrictions: form.dietaryRestrictions.trim() || null,
        })}
      >
        Save
      </button>
    </div>
  );
};

const AssessmentEventDetails: React.FC = () => {
  const { id } = useParams();
  const assessmentId = useMemo(() => parseInt(id || '0', 10), [id]);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [expandedDietary, setExpandedDietary] = useState<Record<string, boolean>>({});

  const hasRole = (roleName: string) => (
    user?.roles?.some((role) => (typeof role === 'string' ? role === roleName : role.name === roleName)) || false
  );
  const isAdmin = hasRole('admin') || hasRole('assessment-admin');
  const canViewEventDetails = isAdmin || hasRole('staff');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['assessments', assessmentId, 'event-details'],
    queryFn: () => assessmentsService.getEventDetails(assessmentId),
    enabled: canViewEventDetails && Number.isFinite(assessmentId) && assessmentId > 0,
  });

  const assessmentQuery = useQuery({
    queryKey: ['assessments', assessmentId, 'detail'],
    queryFn: () => assessmentsService.get(assessmentId),
    enabled: canViewEventDetails && Number.isFinite(assessmentId) && assessmentId > 0,
  });

  const createMutation = useMutation({
    mutationFn: (payload: AttendancePayload) => assessmentsService.createAttendance(assessmentId, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['assessments', assessmentId, 'event-details'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { attendanceId: number; payload: AttendancePayload }) =>
      assessmentsService.updateAttendance(assessmentId, vars.attendanceId, vars.payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['assessments', assessmentId, 'event-details'] });
    },
  });

  const [newEntry, setNewEntry] = useState({
    displayName: '',
    email: '',
    phone: '',
    isAttending: true,
    hotelNeeded: false,
    hotelSundayNight: false,
    hotelMondayNight: false,
    guestCount: 0,
    guestNames: '',
    dietaryRestrictions: '',
  });

  const eventDetails = data?.data;
  const attendanceEntries = eventDetails?.attendance?.entries || [];
  const attendanceByUserId = useMemo(() => {
    const map = new Map<number, AssessmentAttendanceEntry>();
    attendanceEntries.forEach((entry) => {
      if (entry.userId) map.set(entry.userId, entry);
    });
    return map;
  }, [attendanceEntries]);

  const candidateRows = useMemo(() => {
    const participants = assessmentQuery.data?.data?.participants || [];
    return participants
      .filter((p: any) => p.participantType === 'portal_candidate')
      .map((p: any) => {
        const c = p.portalCandidate || {};
        const name = c.name || [c.firstName, c.lastName].filter(Boolean).join(' ') || '—';
        return {
          id: p.id,
          userId: c.user?.id || null,
          name,
          phone: c.phone || '—',
          hotelNeeded: null,
          hotelSundayNight: null,
          hotelMondayNight: null,
          guestCount: null,
          guestNames: [],
          dietaryRestrictions: null,
        };
      });
  }, [assessmentQuery.data?.data?.participants]);

  const assignedRoleRows = useMemo(() => {
    const rows = (eventDetails?.assignedRoles?.users || []).map((assigned) => {
      const attendance = attendanceByUserId.get(assigned.id);
      return {
        id: assigned.id,
        name: `${assigned.firstName} ${assigned.lastName}`.trim() || assigned.email,
        phone: assigned.phone || '—',
        hotelNeeded: attendance?.hotelNeeded ?? null,
        hotelSundayNight: attendance?.hotelSundayNight ?? null,
        hotelMondayNight: attendance?.hotelMondayNight ?? null,
        guestCount: attendance?.guestCount ?? null,
        guestNames: attendance?.guestNames || [],
      };
    });
    return rows;
  }, [eventDetails?.assignedRoles?.users, attendanceByUserId]);

  const assignedRoleNamesCount = eventDetails?.assignedRoles?.names?.length || 0;
  const assignedUserIds = useMemo(() => new Set(assignedRoleRows.map((r) => r.id)), [assignedRoleRows]);
  const candidateUserIds = useMemo(
    () => new Set(candidateRows.map((row) => row.userId).filter(Boolean) as number[]),
    [candidateRows]
  );

  const otherConfirmedRows = useMemo(() => (
    attendanceEntries
      .filter((entry) => (
        entry.userId &&
        !assignedUserIds.has(entry.userId) &&
        !candidateUserIds.has(entry.userId) &&
        entry.isAttending !== false
      ))
      .map((entry) => ({
        id: entry.id,
        name: entry.resolvedName || entry.displayName || entry.email || '—',
        phone: entry.phone || entry.user?.phone || '—',
        hotelNeeded: entry.hotelNeeded,
        hotelSundayNight: entry.hotelSundayNight,
        hotelMondayNight: entry.hotelMondayNight,
        guestCount: entry.guestCount,
        guestNames: entry.guestNames || [],
        dietaryRestrictions: entry.dietaryRestrictions || null,
      }))
  ), [attendanceEntries, assignedUserIds, candidateUserIds]);

  const manualAddedRows = useMemo(() => (
    attendanceEntries
      .filter((entry) => !entry.userId && entry.isAttending !== false)
      .map((entry) => ({
        id: entry.id,
        name: entry.displayName || entry.email || '—',
        phone: entry.phone || '—',
        hotelNeeded: entry.hotelNeeded,
        hotelSundayNight: entry.hotelSundayNight,
        hotelMondayNight: entry.hotelMondayNight,
        guestCount: entry.guestCount,
        guestNames: entry.guestNames || [],
      }))
  ), [attendanceEntries]);

  const assignedGuestCount = useMemo(
    () => assignedRoleRows.reduce((sum, row) => sum + (row.guestCount || 0), 0),
    [assignedRoleRows]
  );
  const otherGuestCount = useMemo(
    () => otherConfirmedRows.reduce((sum, row) => sum + (row.guestCount || 0), 0),
    [otherConfirmedRows]
  );
  const manualGuestCount = useMemo(
    () => manualAddedRows.reduce((sum, row) => sum + (row.guestCount || 0), 0),
    [manualAddedRows]
  );

  const candidatesSpousesTotal = (eventDetails?.totals?.candidates || 0) + (eventDetails?.totals?.spouses || 0);
  const assigneesTotal = (assignedRoleRows.length + assignedRoleNamesCount) + assignedGuestCount;
  const othersTotal = otherConfirmedRows.length + otherGuestCount;
  const manualTotal = manualAddedRows.length + manualGuestCount;
  const fullTotal = candidatesSpousesTotal + assigneesTotal + othersTotal + manualTotal;

  const dietaryEntries = eventDetails?.attendance?.dietary || [];
  const hotelEntries = useMemo(() => (
    attendanceEntries
      .filter((entry) => entry.isAttending !== false && (entry.hotelNeeded || entry.hotelSundayNight || entry.hotelMondayNight))
      .map((entry) => ({
        id: entry.id,
        name: entry.resolvedName || entry.displayName || entry.email || '—',
        sunday: entry.hotelSundayNight,
        monday: entry.hotelMondayNight,
      }))
  ), [attendanceEntries]);

  const renderGuestInfo = (guestNames: string[]) => {
    if (!guestNames || guestNames.length === 0) return null;
    return (
      <span
        className="ml-2 inline-flex items-center justify-center h-4 w-4 rounded-full border border-gray-300 text-[10px] text-gray-600"
        title={guestNames.join(', ')}
      >
        i
      </span>
    );
  };

  const renderDietaryCell = (key: string, dietary?: string | null) => {
    const text = (dietary || '').trim();
    if (!text) return '—';
    const isExpanded = expandedDietary[key];
    const limit = 60;
    const shouldTruncate = text.length > limit;
    const display = !shouldTruncate || isExpanded ? text : `${text.slice(0, limit)}…`;
    return (
      <div className="flex items-start gap-2">
        <span className="text-gray-700 whitespace-pre-wrap">{display}</span>
        {shouldTruncate && (
          <button
            type="button"
            className="text-xs text-primary-600 hover:text-primary-700 whitespace-nowrap"
            onClick={() => setExpandedDietary((prev) => ({ ...prev, [key]: !prev[key] }))}
          >
            {isExpanded ? 'See less' : 'See more'}
          </button>
        )}
      </div>
    );
  };

  const renderHotelLabel = (row: { hotelNeeded: boolean | null; hotelSundayNight?: boolean | null; hotelMondayNight?: boolean | null }) => {
    if (row.hotelNeeded === null) return '—';
    if (!row.hotelNeeded) return 'No';
    const sun = row.hotelSundayNight;
    const mon = row.hotelMondayNight;
    if (sun && mon) return 'Yes (Sun + Mon)';
    if (sun) return 'Yes (Sun)';
    if (mon) return 'Yes (Mon)';
    return 'Yes';
  };

  if (!canViewEventDetails) {
    return (
      <Layout>
        <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 text-gray-700">
          You do not have access to this page.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Event Details</h1>
            <p className="text-gray-600 mt-1">Attendance totals and confirmations.</p>
          </div>
          <Link to={`/assessments/${assessmentId}`} className="text-sm text-primary-600 hover:text-primary-700">
            Back to Assessment
          </Link>
        </div>

        {isLoading && (
          <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 text-gray-700">Loading…</div>
        )}

        {isError && (
          <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 text-red-700">Failed to load event details.</div>
        )}

        {!isLoading && !isError && eventDetails && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white shadow-sm rounded-xl p-4">
                <div className="text-sm text-gray-500">Candidates + spouses</div>
                <div className="text-2xl font-semibold text-gray-900">{candidatesSpousesTotal}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {eventDetails.totals.candidates} + {eventDetails.totals.spouses} = {candidatesSpousesTotal}
                </div>
              </div>
              <div className="bg-white shadow-sm rounded-xl p-4">
                <div className="text-sm text-gray-500">Assignees + guests</div>
                <div className="text-2xl font-semibold text-gray-900">{assigneesTotal}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {assignedRoleRows.length + assignedRoleNamesCount} + {assignedGuestCount} = {assigneesTotal}
                </div>
              </div>
              <div className="bg-white shadow-sm rounded-xl p-4">
                <div className="text-sm text-gray-500">Others + guests</div>
                <div className="text-2xl font-semibold text-gray-900">{othersTotal}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {otherConfirmedRows.length} + {otherGuestCount} = {othersTotal}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white shadow-sm rounded-xl p-4">
                <div className="text-sm text-gray-500">Manually added</div>
                <div className="text-2xl font-semibold text-gray-900">{manualTotal}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {manualAddedRows.length} + {manualGuestCount} = {manualTotal}
                </div>
              </div>
              <div className="bg-white shadow-sm rounded-xl p-4">
                <div className="text-sm text-gray-500">Total attendees</div>
                <div className="text-3xl font-semibold text-gray-900">{fullTotal}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {candidatesSpousesTotal} + {assigneesTotal} + {othersTotal} + {manualTotal} = {fullTotal}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 space-y-3">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Hotel Requests</h2>
                  <p className="text-sm text-gray-600">Who needs a hotel and which nights.</p>
                </div>
                {hotelEntries.length === 0 ? (
                  <div className="text-sm text-gray-500">No hotel requests yet.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                        <tr>
                          <th className="px-4 py-2 text-left font-semibold">Name</th>
                          <th className="px-4 py-2 text-left font-semibold">Nights</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {hotelEntries.map((entry) => (
                          <tr key={`hotel-${entry.id}`}>
                            <td className="px-4 py-2 text-gray-900">{entry.name}</td>
                            <td className="px-4 py-2 text-gray-700">
                              {entry.sunday && entry.monday ? 'Sun + Mon' : entry.sunday ? 'Sun' : entry.monday ? 'Mon' : 'Requested'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 space-y-3">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Dietary Considerations</h2>
                  <p className="text-sm text-gray-600">Reported dietary restrictions.</p>
                </div>
                {dietaryEntries.length === 0 ? (
                  <div className="text-sm text-gray-500">No dietary restrictions reported.</div>
                ) : (
                  <div className="space-y-2 text-sm text-gray-700">
                    {dietaryEntries.map((entry, index) => (
                      <div key={`dietary-${index}`} className="border border-gray-200 rounded-lg px-3 py-2">
                        <div className="font-medium text-gray-900">{entry.name}</div>
                        <div className="text-gray-600 mt-1 whitespace-pre-wrap">{entry.details}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Attendance Overview</h2>
                <p className="text-sm text-gray-600">Grouped totals with hotel and guest details.</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold">Name</th>
                      <th className="px-4 py-2 text-left font-semibold">Hotel?</th>
                      <th className="px-4 py-2 text-left font-semibold">Guest #</th>
                      <th className="px-4 py-2 text-left font-semibold">Dietary</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="bg-blue-50">
                      <td colSpan={4} className="px-4 py-2 text-xs font-semibold text-gray-600">Candidates</td>
                    </tr>
                    {candidateRows.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-4 py-2 text-gray-500">No candidates.</td>
                      </tr>
                    )}
                    {candidateRows.map((row) => {
                      const attendance = row.userId ? attendanceByUserId.get(row.userId) : null;
                      return (
                        <tr key={`candidate-${row.id}`}>
                          <td className="px-4 py-2 text-gray-900">{row.name}</td>
                          <td className="px-4 py-2 text-gray-700">
                            {attendance ? renderHotelLabel(attendance) : '—'}
                          </td>
                          <td className="px-4 py-2 text-gray-700">
                            {attendance ? (attendance.guestCount || 0) : '—'}
                            {attendance ? renderGuestInfo(attendance.guestNames || []) : null}
                          </td>
                          <td className="px-4 py-2 text-gray-700">
                            {renderDietaryCell(`candidate-${row.id}`, attendance?.dietaryRestrictions)}
                          </td>
                        </tr>
                      );
                    })}

                    <tr className="bg-purple-50">
                      <td colSpan={4} className="px-4 py-2 text-xs font-semibold text-gray-600">Assigned roles at assessment</td>
                    </tr>
                    {assignedRoleRows.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-4 py-2 text-gray-500">No assigned roles.</td>
                      </tr>
                    )}
                    {assignedRoleRows.map((row) => {
                      const attendance = attendanceByUserId.get(row.id);
                      return (
                        <tr key={`assigned-${row.id}`}>
                          <td className="px-4 py-2 text-gray-900">{row.name}</td>
                          <td className="px-4 py-2 text-gray-700">{renderHotelLabel(row)}</td>
                          <td className="px-4 py-2 text-gray-700">
                            {row.guestCount === null ? '—' : row.guestCount}
                            {renderGuestInfo(row.guestNames)}
                          </td>
                          <td className="px-4 py-2 text-gray-700">
                            {renderDietaryCell(`assigned-${row.id}`, attendance?.dietaryRestrictions)}
                          </td>
                        </tr>
                      );
                    })}

                    <tr className="bg-amber-50">
                      <td colSpan={4} className="px-4 py-2 text-xs font-semibold text-gray-600">Other who confirmed attendance</td>
                    </tr>
                    {otherConfirmedRows.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-4 py-2 text-gray-500">No additional confirmations.</td>
                      </tr>
                    )}
                    {otherConfirmedRows.map((row) => {
                      const attendance = attendanceEntries.find((entry) => entry.id === row.id);
                      return (
                        <tr key={`other-${row.id}`}>
                          <td className="px-4 py-2 text-gray-900">{row.name}</td>
                          <td className="px-4 py-2 text-gray-700">{renderHotelLabel(row)}</td>
                          <td className="px-4 py-2 text-gray-700">
                            {row.guestCount}
                            {renderGuestInfo(row.guestNames)}
                          </td>
                          <td className="px-4 py-2 text-gray-700">
                            {renderDietaryCell(`other-${row.id}`, attendance?.dietaryRestrictions)}
                          </td>
                        </tr>
                      );
                    })}

                    <tr className="bg-emerald-50">
                      <td colSpan={4} className="px-4 py-2 text-xs font-semibold text-gray-600">People manually added</td>
                    </tr>
                    {manualAddedRows.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-4 py-2 text-gray-500">No manual entries.</td>
                      </tr>
                    )}
                    {manualAddedRows.map((row) => {
                      const attendance = attendanceEntries.find((entry) => entry.id === row.id);
                      return (
                        <tr key={`manual-${row.id}`}>
                          <td className="px-4 py-2 text-gray-900">{row.name}</td>
                          <td className="px-4 py-2 text-gray-700">{renderHotelLabel(row)}</td>
                          <td className="px-4 py-2 text-gray-700">
                            {row.guestCount}
                            {renderGuestInfo(row.guestNames)}
                          </td>
                          <td className="px-4 py-2 text-gray-700">
                            {renderDietaryCell(`manual-${row.id}`, attendance?.dietaryRestrictions)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {isAdmin && (
              <>
                <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 space-y-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Assigned Role Attendance</h2>
                    <p className="text-sm text-gray-600">Confirm assigned users even if they have no RSVP yet.</p>
                  </div>
                  <div className="space-y-2">
                    {(eventDetails.assignedRoles.users || []).map((assigned) => {
                      const attendance = attendanceByUserId.get(assigned.id);
                      return (
                        <div key={assigned.id} className="flex items-center justify-between gap-4 border border-gray-200 rounded-lg px-4 py-3">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {assigned.firstName} {assigned.lastName}
                            </div>
                            <div className="text-xs text-gray-500">
                              {assigned.roleName || 'Assigned role'} • {assigned.email}
                            </div>
                          </div>
                          {attendance ? (
                            <span className="text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded-full">
                              Confirmed
                            </span>
                          ) : (
                            <button
                              className="text-xs px-3 py-1 rounded-full bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                              disabled={createMutation.isPending}
                              onClick={() => createMutation.mutate({ userId: assigned.id, isAttending: true })}
                            >
                              Confirm
                            </button>
                          )}
                        </div>
                      );
                    })}
                    {(eventDetails.assignedRoles.names || []).length > 0 && (
                      <div className="text-sm text-gray-600">
                        Assigned without user accounts: {(eventDetails.assignedRoles.names || []).map((n) => n.name).join(', ')}
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 space-y-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Attendance Responses</h2>
                    <p className="text-sm text-gray-600">Edit confirmations, hotels, guests, and dietary notes.</p>
                  </div>
                  <div className="space-y-4">
                    {attendanceEntries.length === 0 && (
                      <div className="text-sm text-gray-500">No attendance responses yet.</div>
                    )}
                    {attendanceEntries.map((entry) => (
                      <AttendanceEditor
                        key={entry.id}
                        entry={entry}
                        isSaving={updateMutation.isPending}
                        onSave={(attendanceId, payload) => updateMutation.mutate({ attendanceId, payload })}
                      />
                    ))}
                  </div>
                </div>

                <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 space-y-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Add attendee</h2>
                    <p className="text-sm text-gray-600">Add someone not already listed.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input
                      className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder="Name"
                      value={newEntry.displayName}
                      onChange={(e) => setNewEntry((prev) => ({ ...prev, displayName: e.target.value }))}
                    />
                    <input
                      className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder="Email"
                      value={newEntry.email}
                      onChange={(e) => setNewEntry((prev) => ({ ...prev, email: e.target.value }))}
                    />
                    <input
                      className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder="Phone"
                      value={newEntry.phone}
                      onChange={(e) => setNewEntry((prev) => ({ ...prev, phone: e.target.value }))}
                    />
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={newEntry.isAttending}
                        onChange={(e) => setNewEntry((prev) => ({ ...prev, isAttending: e.target.checked }))}
                      />
                      Attending
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={newEntry.hotelNeeded}
                        onChange={(e) => setNewEntry((prev) => ({ ...prev, hotelNeeded: e.target.checked }))}
                      />
                      Hotel needed
                    </label>
                    {newEntry.hotelNeeded && (
                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={newEntry.hotelSundayNight}
                            onChange={(e) => setNewEntry((prev) => ({ ...prev, hotelSundayNight: e.target.checked }))}
                          />
                          Sunday night
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={newEntry.hotelMondayNight}
                            onChange={(e) => setNewEntry((prev) => ({ ...prev, hotelMondayNight: e.target.checked }))}
                          />
                          Monday night
                        </label>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input
                      className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                      type="number"
                      min={0}
                      placeholder="Guest count"
                      value={newEntry.guestCount}
                      onChange={(e) => setNewEntry((prev) => ({ ...prev, guestCount: Number(e.target.value) }))}
                    />
                    <input
                      className="border border-gray-300 rounded-md px-3 py-2 text-sm md:col-span-2"
                      placeholder="Guest names (comma separated)"
                      value={newEntry.guestNames}
                      onChange={(e) => setNewEntry((prev) => ({ ...prev, guestNames: e.target.value }))}
                    />
                  </div>
                  <textarea
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm w-full"
                    rows={2}
                    placeholder="Dietary restrictions"
                    value={newEntry.dietaryRestrictions}
                    onChange={(e) => setNewEntry((prev) => ({ ...prev, dietaryRestrictions: e.target.value }))}
                  />
                  <button
                    className="inline-flex items-center px-4 py-2 rounded-md bg-primary-600 text-white text-sm hover:bg-primary-700 disabled:opacity-50"
                    disabled={createMutation.isPending || (!newEntry.displayName.trim() && !newEntry.email.trim())}
                    onClick={() => createMutation.mutate({
                      displayName: newEntry.displayName.trim() || null,
                      email: newEntry.email.trim() || null,
                      phone: newEntry.phone.trim() || null,
                      isAttending: newEntry.isAttending,
                      hotelNeeded: newEntry.hotelNeeded,
                      hotelSundayNight: newEntry.hotelSundayNight,
                      hotelMondayNight: newEntry.hotelMondayNight,
                      guestCount: newEntry.guestCount || 0,
                      guestNames: newEntry.guestNames,
                      dietaryRestrictions: newEntry.dietaryRestrictions.trim() || null,
                    })}
                  >
                    Add attendee
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default AssessmentEventDetails;
